import { DialogRef } from '@angular/cdk/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { ModalService } from '@src/shared/modal/modal.service';
import { SubscriptionHandler } from '../../advanced_campaign_booking.module/components/subscription-handler';
import { AdServerService } from '../../woo_services.module/AdServerService';
import { AuthService } from '../../woo_services.module/AuthService';
import { CampaignService } from '../../woo_services.module/CampaignService';
import { DashboardModel, DashboardStore } from '../../woo_services.module/DashboardStore';
import { GoogleTagManagerService } from '../../woo_services.module/GoogleTagManagerService';
import { SystemService, SystemSettings } from '../../woo_services.module/SystemService';
import { TranslationService } from '../../woo_services.module/TranslationService';
import {
  Campaign,
  CampaignStatus,
  DashboardTab,
  SendToAdServerJobResult,
  Targeting,
  wooId,
} from '../../woo_services.module/shared-types';

// Job failures where the creatives were still updated, but something has to be fixed in MRM.
const UPDATED_WITH_AD_SERVER_WARNING_CODES = [
  'schedules_in_flighting_mode_present',
  'creative_instances_not_deactivated',
];

@Component({
  selector: 'creatives-details',
  templateUrl: './creatives-details.component.html',
  standalone: false,
})
export class CreativesDetails extends SubscriptionHandler implements OnDestroy {
  readonly adminOrPlanner: boolean;
  readonly convertEditCreativesCriterium: (criterium: string) => string;

  editCreatives = false;
  numberOfDaysToEditForAgency = 4;
  targetings: Targeting[];
  notAllowedToEditCreativesReason: string;
  canSave: boolean;
  private isGamblingCampaign: boolean;
  get hasBeenSentToAdServer(): boolean {
    return this.campaignService.hasAnyStatus(this.dashboardStore.state.activeCampaign, [
      CampaignStatus.ongoing,
      CampaignStatus.upcoming,
    ]);
  }
  get saveButtonText(): string {
    return this.authService.adminOrPlanner() && this.hasBeenSentToAdServer
      ? 'Uppdatera och skicka till adservern'
      : 'Spara';
  }

  constructor(
    private adServerService: AdServerService,
    private authService: AuthService,
    private campaignService: CampaignService,
    private dashboardStore: DashboardStore,
    private modalService: ModalService,
    private googleTagManagerService: GoogleTagManagerService,
    private translationService: TranslationService,
    private sytemService: SystemService,
  ) {
    super();
    this.adminOrPlanner = this.authService.adminOrPlanner();
    this.convertEditCreativesCriterium = this.translationService.convertEditCreativesCriterium;
    dashboardStore.state$.subscribe(this.initFromStore);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancelEditCreatives();
  }

  initFromStore = (model: DashboardModel): void => {
    // Timeout to avoid values changing after being checked.
    setTimeout(() => {
      if (model.activeCampaign) {
        this.targetings = model.activeCampaign.targetings;
        this.isGamblingCampaign = !!model.activeCampaign.gambling_campaign;
      }
      this.editCreatives = model.editCreatives;
      this.notAllowedToEditCreativesReason = model.notAllowedToEditCreativesReason;
      this.canSave = model.modifiedCreatives
        ? Object.values(model.modifiedCreatives)
            .flatMap((targetingObj) => Object.values(targetingObj).map((creativeObj) => creativeObj.valid))
            .every((value) => value)
        : false;
      if (!this.notAllowedToEditCreativesReason && !this.adminOrPlanner) {
        this.sytemService.loadSystemwideSettings().then((settings: SystemSettings) => {
          this.numberOfDaysToEditForAgency = this.isGamblingCampaign
            ? settings.min_days_to_update_gambling_creatives_in_mail
            : settings.min_days_to_update_creatives_in_mail;
        });
      }
    });
  };

  startEditCreatives(): void {
    this.googleTagManagerService.clickedEditAllCreatives();
    this.dashboardStore.setEditCreatives(true);
  }

  cancelEditCreatives(): void {
    this.dashboardStore.setEditCreatives(false);
  }

  saveEditCreatives(): void {
    if (!this.canSave) {
      return;
    }

    const campaign = this.dashboardStore.state.activeCampaign;

    const hasChanges = campaign.targetings.some((t) =>
      Object.values(this.dashboardStore.state.modifiedCreatives[t.id])
        .map((c) => c.modified)
        .some((modified) => modified),
    );

    if (!hasChanges) {
      this.modalService.openSuccess('Det här gick lättare än förväntat, inga ändringar att spara :)');
      this.dashboardStore.setEditCreatives(false);
      return;
    }

    if (campaign.status === CampaignStatus.ongoing) {
      this.updateOngoingCreatives(campaign);
    } else {
      this.updateCreatives(campaign);
    }
  }

  getTargetingExpandedFromStore(targetingId: wooId): boolean {
    const expandedTargetings = this.dashboardStore.state.expandedTargetingHeaders?.[DashboardTab.creatives];
    return expandedTargetings?.[targetingId] ?? true;
  }

  onExpandedChange(expanded: boolean, targetingId: wooId): void {
    this.dashboardStore.setExpandedTargetingHeader(DashboardTab.creatives, targetingId, expanded);
  }

  private updateCreatives(campaign: Campaign) {
    const loaderModal = this.modalService.openLoader('Uppdaterar material');
    // Store changes in all targetings
    const updateRequests = campaign.targetings.map((t) => {
      const creatives = Object.values(this.dashboardStore.state.modifiedCreatives[t.id]).map(
        (creativeObj) => creativeObj.creative,
      );
      return this.campaignService.updateCreatives(campaign.id, t.id, creatives);
    });
    Promise.all(updateRequests)
      .then(() => {
        this.reloadCampaignFromServer();
        this.dashboardStore.setEditCreatives(false);
        if (campaign.status === CampaignStatus.upcoming) {
          this.sendToAdServer(campaign.id);
        } else {
          this.modalService.openSuccess('Din kampanj är nu uppdaterad.');
        }
      })
      .catch((err) => {
        console.error('Failed updating material:', err.error);
        // HTTP errors are shown by the global error handler, failed jobs are not
        if (!(err instanceof HttpErrorResponse)) {
          const reason = err.error?.error ? [this.translationService.convertError(err.error.error)] : [];
          this.modalService.openError('Ajdå, något gick snett. Kunde inte uppdatera material.', ...reason);
        }
      })
      .finally(() => {
        loaderModal.close();
      });
  }

  private updateOngoingCreatives(campaign: Campaign) {
    this.modalService
      .openConfirm({
        title: 'Uppdatera material på adservern',
        texts: [
          'Ändringarna du har begärt kommer att medföra uppdateringar på adservern.',
          'Vill du genomföra ändringarna?',
        ],
        confirmLabel: 'Uppdatera',
        cancelLabel: 'Avbryt',
      })
      .then(
        async (response) => {
          if (!response.confirm) return;

          const loaderModal = this.modalService.openLoader('Uppdaterar material');

          // Store changes in all targetings
          let hasFailure = false;
          for (const targeting of campaign.targetings) {
            const creatives = Object.values(this.dashboardStore.state.modifiedCreatives[targeting.id])
              .filter((creativeObj) => creativeObj.modified)
              .map((creativeObj) => creativeObj.creative);
            if (creatives.length < 1) continue;
            await this.campaignService.updateOngoingCreatives(campaign.id, targeting.id, creatives).catch((err) => {
              hasFailure = true;
              // A failed PATCH rejects with an HttpErrorResponse (err.error is the response body,
              // e.g. {error: 'campaign_already_being_booked'}), but a failed job instead rejects
              // with the job's result directly (err.error is already the message string).
              const errorCode = typeof err.error === 'string' ? err.error : err.error?.error;
              // Several warnings from the same job are joined with '; '.
              const errorCodes: string[] = typeof errorCode === 'string' ? errorCode.split('; ') : [];
              if (
                errorCodes.length > 0 &&
                errorCodes.every((code) => UPDATED_WITH_AD_SERVER_WARNING_CODES.includes(code))
              ) {
                this.modalService.openWarning(
                  'Materialet har uppdaterats, men kräver åtgärd i MRM',
                  errorCodes.map((code) => this.translationService.convertError(code)).join(' '),
                );
              } else {
                this.modalService.openError(
                  'Ajdå, något gick snett. Kunde inte uppdatera material.',
                  this.translationService.convertError(errorCode),
                );
              }
            });
          }

          if (!hasFailure) {
            this.modalService.openSuccess('Din kampanj är nu uppdaterad.');
          }
          this.dashboardStore.setEditCreatives(false);
          this.reloadCampaignFromServer();
          loaderModal.close();
        },
        () => null,
      )
      .catch((err) => {
        // TODO: https://teliatv4media.atlassian.net/browse/WOO-1625
        if (err.error === 'schedules_in_flighting_mode_present') {
          console.error(err);
          this.modalService.openError(this.translationService.convertError(err.error));
          this.dashboardStore.setEditCreatives(false);
          this.reloadCampaignFromServer();
        } else {
          console.error('Failed updating material: ', err);
          this.modalService.openError(this.translationService.convertError(err.error));
        }
      });
  }

  private reloadCampaignFromServer = () => {
    this.campaignService.get(this.dashboardStore.state.activeCampaign.id).then((updatedCampaign) => {
      updatedCampaign.targetings.forEach((updatedTargeting) =>
        this.dashboardStore.setCampaignCreatives(updatedTargeting.id, updatedTargeting.creatives),
      );
    });
  };

  private sendToAdServer(campaignId: wooId) {
    let loaderModal: DialogRef<unknown, unknown>;
    if (this.adminOrPlanner) {
      loaderModal = this.modalService.openLoader('Skickar till adservern');
    }
    this.adServerService
      .sendCampaign(campaignId)
      .then(this.sendToAdServerSuccess, this.sendToAdServerError)
      .finally(() => {
        if (loaderModal) {
          loaderModal.close();
        }
      });
  }

  private sendToAdServerSuccess = () => {
    const msg = this.adminOrPlanner ? 'Kampanjen har skickats till adservern.' : 'Din kampanj är nu uppdaterad.';
    this.modalService.openSuccess(msg);
  };

  private sendToAdServerError = (errorResponse: SendToAdServerJobResult) => {
    if (this.adminOrPlanner) {
      if (!!errorResponse?.data_errors) {
        this.modalService.openDetailedError(
          this.translationService.convertDetailedError(errorResponse.error),
          errorResponse.data_errors,
        );
        return;
      }

      const msg = this.translationService.convertSendToAdserverError(errorResponse.error);
      this.modalService.openError(msg);
    } else {
      this.modalService.openSuccess('Din kampanj är nu uppdaterad.');
    }
  };
}
